import {Center} from 'src/design/atoms/Center';
import {Loader} from 'src/design/atoms/Loader';
import {Stack} from 'src/design/atoms/Stack';
import {useParams} from 'react-router-dom';
import {
  FeatureFlagEnum,
  ModuleEnum,
  PermissionOperationEnum,
} from 'src/api/ProactApiClient';
import {InfoCard} from 'src/design/molecules/InfoCard';
import {
  CalendarIcon,
  CodeIcon,
  HashIcon,
  IconSize,
  InfoIcon,
  UserIcon,
} from 'src/design/atoms/icons/CommonIcons';
import {ActionCard} from 'src/design/molecules/ActionCard';
import {BackBar} from 'src/app/layout/BackBar';
import {ContentWrapper} from 'src/app/layout/ContentWrapper';
import {DetailGrid} from 'src/design/atoms/DetailGrid';
import {PageHeader} from 'src/design/organisms/PageHeader';
import {SidebarLinkAuditLog} from 'src/app/layout/Sidebar/SidebarLinksConfig';
import {AuditLogDiff} from 'src/app/audit-logs/AuditLogDiff';
import {AuditLogDocumentTitle} from 'src/app/audit-logs/auditLogHelpers';
import {routeRegistry} from 'src/pages/RouteRegistry';
import {useGetAuditLogQuery} from 'src/redux/queries/auditLog';
import {PathUtility} from 'src/shared/constants/PathBuilder';
import useSetDocumentTitle from 'src/shared/hooks/useSetDocumentTitle';

export const ViewAuditLogPage = () => {
  useSetDocumentTitle(AuditLogDocumentTitle);
  const {id} = useParams();
  const {data, isLoading} = useGetAuditLogQuery(Number(id), {skip: id === undefined});

  const getActionBadgeColor = (actionType?: string) => {
    switch (actionType) {
      case 'Added':
        return 'green';
      case 'Modified':
        return 'blue';
      case 'Deleted':
        return 'red';
      default:
        return 'gray';
    }
  };

  if (isLoading) {
    return (
      <ContentWrapper featureFlag={FeatureFlagEnum.AuditLogs}>
        <Center py="xl">
          <Loader size="lg" />
        </Center>
      </ContentWrapper>
    );
  }

  return (
    <ContentWrapper featureFlag={FeatureFlagEnum.AuditLogs}>
      <Stack gap="lg">
        <BackBar
          fallbackTo={PathUtility.AuditLogs()}
          fallbackLabel="Back to Audit Logs"
          breadcrumbs={[
            {label: 'Audit Logs', to: PathUtility.AuditLogs()},
            {label: `Log #${id}`},
          ]}
        />

        <PageHeader>
          <PageHeader.Title
            icon={SidebarLinkAuditLog.icon}
            subtitle={data?.entityName}>
            Audit Log #{id}
          </PageHeader.Title>
          <PageHeader.Badges>
            <PageHeader.Badge
              color={getActionBadgeColor(data?.actionType)}
              variant="light">
              {data?.actionType}
            </PageHeader.Badge>
          </PageHeader.Badges>
        </PageHeader>

        <DetailGrid>
          <DetailGrid.Main>
            {id && <AuditLogDiff audit={data} header={<></>} hideDescription />}
          </DetailGrid.Main>

          <DetailGrid.Sidebar>
            <InfoCard
              title="Entity Details"
              icon={<HashIcon size={IconSize.sm} />}
              compact>
              <InfoCard.Row
                icon={<HashIcon size={IconSize.sm} />}
                label="Entity ID"
                value={data?.entityId ?? '—'}
              />
              <InfoCard.Row
                icon={<CodeIcon size={IconSize.sm} />}
                label="Entity Type"
                value={data?.entityName}
              />
              <InfoCard.Row
                icon={<UserIcon size={IconSize.sm} />}
                label="Changed By"
                value={data?.creator?.name ?? 'System'}
              />
              <InfoCard.Row
                icon={<CalendarIcon size={IconSize.sm} />}
                label="Changed At"
                value={data?.createdAt?.toString().toLongDate()}
              />
            </InfoCard>

            <ActionCard
              title="About Audit Data"
              icon={<InfoIcon size={IconSize.sm} />}>
              <ActionCard.Description>
                Audit data is system generated and managed. Field names and
                values may appear different than elsewhere. Audit data is
                automatically deleted based on your environment&apos;s data
                retention policy. All dates shown in the properties table are in
                UTC timezone.
              </ActionCard.Description>
            </ActionCard>
          </DetailGrid.Sidebar>
        </DetailGrid>
      </Stack>
    </ContentWrapper>
  );
};

// Register the route with the new system
routeRegistry.registerPageRoute({
  backNavigation: 'page',
  path: PathUtility.AuditLogView(':id'),
  element: <ViewAuditLogPage />,
  module: ModuleEnum.AuditLog,
  operation: PermissionOperationEnum.View,
  isPrivate: true,
});

export default ViewAuditLogPage;
